import { motion } from "framer-motion";
import { Camera } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { TIMELINE } from "../data/content";

const ease = [0.22, 1, 0.36, 1];

const PHOTOS = [
  {
    src: "/images/floods-truck.jpg",
    alt: "Hope Giver team distributing relief supplies from a truck during the Punjab floods",
    caption: "Relief truck · Punjab floods",
  },
  {
    src: "/images/floods-handing.jpg",
    alt: "Hope Giver volunteer handing food and essentials to a family during the Punjab floods",
    caption: "Hand to hand · ration kits",
  },
  {
    src: "/images/floods-field.jpg",
    alt: "Volunteers reaching families beside flooded fields in Punjab",
    caption: "Beside flooded fields",
  },
];

export default function Impact() {
  return (
    <section
      id="impact"
      className="grain relative overflow-hidden bg-[#08353D] py-16 sm:py-24 lg:py-32"
      data-testid="impact-section"
    >
      <div className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          dark
          align="center"
          eyebrow="Our Impact"
          title="Small Actions."
          accent="Big Impact."
          sub="Our work so far — flood relief in Punjab, Soch Ke Rang 7.0 at the Gymkhana and a visit to Unique Home, with our first exhibition on the way."
        />

        {/* Punjab floods featured — real photos */}
        <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-2 lg:gap-8">
          <motion.figure
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease }}
            className="group relative overflow-hidden rounded-[2rem]"
          >
            <img
              src={PHOTOS[0].src}
              alt={PHOTOS[0].alt}
              loading="lazy"
              className="h-full min-h-[320px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <figcaption className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-[#08353D]/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#F3E096] backdrop-blur">
              <Camera size={14} /> {PHOTOS[0].caption}
            </figcaption>
          </motion.figure>

          <div className="grid grid-rows-2 gap-6">
            {[PHOTOS[1], PHOTOS[2]].map((p, i) => (
              <motion.figure
                key={p.src}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: 0.15 * (i + 1), ease }}
                className="group relative overflow-hidden rounded-[2rem]"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="h-full min-h-[160px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-[#08353D]/80 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#F3E096] backdrop-blur">
                  <Camera size={12} /> {p.caption}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.2, ease }}
          className="mx-auto mt-10 max-w-3xl text-center text-lg leading-relaxed text-white/80"
        >
          When floods struck Punjab, our team loaded trucks with food and essentials and handed
          them directly to affected families — village to village, hand to hand.{" "}
          <span className="font-script text-2xl font-bold text-[#F3E096]">That is Hope Giver.</span>
        </motion.p>

        {/* Timeline */}
        <div className="relative mt-20">
          <div className="absolute left-0 right-0 top-7 hidden h-0.5 bg-gradient-to-r from-[#D4AF37]/20 via-[#D4AF37]/60 to-[#D4AF37]/20 lg:block" />
          <ol className="grid gap-10 lg:grid-cols-5 lg:gap-6">
            {TIMELINE.map((t, i) => (
              <motion.li
                key={t.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: 0.12 * i, ease }}
                className="relative flex gap-5 lg:flex-col lg:items-center lg:gap-0 lg:text-center"
              >
                <span className="z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-[#D4AF37] bg-[#08353D] font-display text-lg font-extrabold text-[#F3E096] shadow-[0_0_0_6px_rgba(8,53,61,1)]">
                  {i + 1}
                </span>
                <div className="lg:mt-5">
                  <h3 className="font-display text-lg font-bold text-white">{t.step}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/60">{t.desc}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
